import * as amplitude from "@amplitude/analytics-browser";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { RiDownloadLine, RiFilter3Line } from "react-icons/ri";
import { useParams, useSearchParams } from "react-router-dom";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { AdminPage } from "src/components/Layout/AdminPage";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { SearchAndFilterTypes } from "src/constants";
import {
  cancelBulkAction,
  exportBulkActionItems,
  getBulkAction,
  listBulkActionItems,
  type ExportBulkActionItemsData,
  type ListBulkActionItemsData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { csvExport } from "src/services/dataTransfer";
import { formatIsoDateToOrg } from "src/services/format";
import * as RD from "src/types/remoteData";
import { DetailFilters } from "./DetailFilters";
import { ActivityItemList } from "./DetailList";
import {
  getActivityStatusMetadata,
  getActivityTitle,
  getBulkActionNoun,
} from "./labels";
import { useMarkFormActivitiesViewed } from "./useUnreadFormActivities";

export const FormActivityDetail = () => {
  const { formTemplateId, bulkActionId } = useParams();
  useMarkFormActivitiesViewed(formTemplateId);
  const organization = useOrganization();
  const { selectedNavFormTemplate } = useFormTemplates();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, setOrderBy } = useOrderByParams();
  const { pagination, setPagination } = usePaginationParams();
  const { isOpen, onToggle } = useDisclosure();
  const {
    isOpen: isCancelOpen,
    onOpen: onOpenCancel,
    onClose: onCloseCancel,
  } = useDisclosure();
  const cancelDialogRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const avelaToast = useAvelaToast();
  const [isExporting, setIsExporting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const searchTerm = searchParams.get(SearchAndFilterTypes.Search) ?? "";
  const schoolIdFilter = searchParams.get("schoolId");
  const statusFilter = searchParams.get("status");
  const messageFilter = searchParams.get("message");

  const activityRD = useRemoteDataRestQuery({
    queryKey: ["bulkAction", bulkActionId],
    queryFn: () =>
      getBulkAction({ path: { bulkActionId: bulkActionId ?? "" } }).then(
        (response) => response.data?.data
      ),
    enabled: !!bulkActionId,
    refetchOnMount: "always",
  });

  const itemsRD = useRemoteDataRestQuery({
    queryKey: [
      "bulkActionItems",
      bulkActionId,
      pagination.limit,
      pagination.offset,
      searchTerm,
      schoolIdFilter,
      statusFilter,
      messageFilter,
      orderBy?.sortKey,
      orderBy?.sortType,
    ],
    queryFn: () =>
      listBulkActionItems({
        path: { bulkActionId: bulkActionId ?? "" },
        query: {
          limit: pagination.limit,
          offset: pagination.offset,
          search: searchTerm || undefined,
          schoolId: schoolIdFilter || undefined,
          status: statusFilter || undefined,
          message: messageFilter || undefined,
          sortKey: orderBy?.sortKey as NonNullable<
            ListBulkActionItemsData["query"]
          >["sortKey"],
          sortType: orderBy?.sortType as NonNullable<
            ListBulkActionItemsData["query"]
          >["sortType"],
        },
      }).then((response) => response.data?.data),
    enabled: !!bulkActionId,
    refetchOnMount: "always",
  });

  const handleSearch = useCallback(
    (term: string) => {
      if (!term) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, term);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearFilters = useCallback(
    (clearSearch: boolean = false) => {
      searchParams.delete("schoolId");
      searchParams.delete("status");
      searchParams.delete("message");
      if (clearSearch) {
        searchParams.delete(SearchAndFilterTypes.Search);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleExportAll = useCallback(async () => {
    if (!bulkActionId || isExporting) {
      return;
    }

    amplitude.track("Clicked export all");
    setIsExporting(true);
    const loadingToastId = avelaToast({
      title: "Export in progress",
      description: "Download will start shortly",
      status: "loading",
    });

    try {
      const records = await exportBulkActionItems({
        path: { bulkActionId },
        query: {
          search: searchTerm || undefined,
          schoolId: schoolIdFilter || undefined,
          status: statusFilter || undefined,
          message: messageFilter || undefined,
          sortKey: orderBy?.sortKey as NonNullable<
            ExportBulkActionItemsData["query"]
          >["sortKey"],
          sortType: orderBy?.sortType as NonNullable<
            ExportBulkActionItemsData["query"]
          >["sortType"],
        },
      });

      if (records.data?.data) {
        csvExport(records.data.data, "activity-items.csv");
        amplitude.track("Completed export all", {
          bulk_action_id: bulkActionId,
        });
      } else {
        amplitude.track("Failed export all", {
          bulk_action_id: bulkActionId,
          error_name: "EmptyResponse",
        });
        avelaToast.genericError();
      }
    } catch (err) {
      const errorName =
        err instanceof Error
          ? err.constructor.name
          : typeof err === "string"
          ? "String"
          : "Unknown";
      amplitude.track("Failed export all", {
        bulk_action_id: bulkActionId,
        error_name: errorName,
      });
      avelaToast.genericError();
    } finally {
      avelaToast.close(loadingToastId);
      setIsExporting(false);
    }
  }, [
    avelaToast,
    bulkActionId,
    isExporting,
    messageFilter,
    orderBy?.sortKey,
    orderBy?.sortType,
    schoolIdFilter,
    searchTerm,
    statusFilter,
  ]);

  const refreshBulkAction = useCallback(async () => {
    if (!bulkActionId) {
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["bulkAction", bulkActionId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["bulkActionItems", bulkActionId],
      }),
    ]);
  }, [bulkActionId, queryClient]);

  const handleCancel = useCallback(async () => {
    if (!bulkActionId || isCancelling) {
      return;
    }

    setIsCancelling(true);
    try {
      const response = await cancelBulkAction({
        path: { bulkActionId },
        throwOnError: false,
      });

      if (!response.response.ok) {
        if (response.response.status === 409) {
          await refreshBulkAction();
          onCloseCancel();
          avelaToast({
            title: "Already finished",
            description:
              response.error?.error.message ??
              "This bulk action is no longer running.",
            status: "warning",
          });
          return;
        }
        avelaToast.genericError();
        return;
      }

      amplitude.track("Cancelled bulk action", {
        bulk_action_id: bulkActionId,
      });
      await refreshBulkAction();
      onCloseCancel();
    } catch (err) {
      console.error(err);
      avelaToast.genericError();
    } finally {
      setIsCancelling(false);
    }
  }, [
    avelaToast,
    bulkActionId,
    isCancelling,
    onCloseCancel,
    refreshBulkAction,
  ]);

  const appliedFiltersCount = [
    schoolIdFilter,
    statusFilter,
    messageFilter,
  ].filter(Boolean).length;

  const filterButtonStyle =
    appliedFiltersCount > 0
      ? {
          variant: "outlineActivated" as const,
          colorScheme: "primary" as const,
        }
      : { variant: "outline" as const, colorScheme: "gray" as const };

  if (!formTemplateId || !bulkActionId) {
    return <NotFound />;
  }

  return (
    <RemoteDataView
      remoteData={RD.toTuple(activityRD, itemsRD)}
      notAsked={<Loading />}
      loading={<Loading />}
      error={() => <GenericError />}
      reloading={null}
    >
      {([activity, page]) => {
        const startedAt = activity.startedAt ?? activity.createdAt;
        const startedBy = activity.createdBy.name;
        const startedAtLabel = formatIsoDateToOrg(
          startedAt,
          organization,
          "h:mm a, MM/dd/yyyy"
        );
        const isCancelable =
          activity.status === "CREATED" || activity.status === "STARTED";

        return (
          <AdminPage
            breadcrumb={
              <Breadcrumb
                items={breadcrumb.form.getBreadcrumbsForActivityDetail(
                  organization,
                  selectedNavFormTemplate,
                  { id: activity.id, label: getActivityTitle(activity) }
                )}
              />
            }
            heading={
              <AdminHeading
                title={getActivityTitle(activity)}
                badge={
                  <StatusTag
                    disableGlossary
                    status={getActivityStatusMetadata(activity)}
                  />
                }
                subtitle={
                  startedBy
                    ? `Started by ${startedBy}, ${startedAtLabel}`
                    : `Started ${startedAtLabel}`
                }
                actionButton={
                  <HStack>
                    <ConditionalTooltip
                      showTooltip={!isCancelable}
                      label="This bulk action is no longer running."
                      shouldWrapChildren
                    >
                      <Button
                        variant="outline"
                        onClick={onOpenCancel}
                        isDisabled={!isCancelable}
                      >
                        Cancel
                      </Button>
                    </ConditionalTooltip>
                    <Button
                      leftIcon={<RiDownloadLine />}
                      variant="solid"
                      isLoading={isExporting}
                      onClick={handleExportAll}
                    >
                      Export All
                    </Button>
                  </HStack>
                }
              />
            }
          >
            <HStack pb={6}>
              <SearchInput
                onSearch={handleSearch}
                value={searchTerm}
                onClear={() => handleSearch("")}
                placeholder="Search by student name, parent/guardian name, or form ID"
              />
              <Button
                {...filterButtonStyle}
                leftIcon={<RiFilter3Line />}
                onClick={onToggle}
              >
                Filter
                {appliedFiltersCount > 0 && `: ${appliedFiltersCount}`}
              </Button>
            </HStack>

            <ActivityItemList
              items={page.items}
              formTemplateId={formTemplateId}
              limit={pagination.limit}
              offset={pagination.offset}
              count={page.total}
              errorCount={page.errorCount}
              hasActiveFilters={appliedFiltersCount > 0 || !!searchTerm}
              onFetchMore={setPagination}
              onChangeSort={setOrderBy}
              onClearFilters={() => handleClearFilters(true)}
              isLoading={itemsRD.isLoading() && !itemsRD.hasData()}
            />

            <AlertDialog
              isOpen={isCancelOpen}
              leastDestructiveRef={cancelDialogRef}
              onClose={onCloseCancel}
              isCentered
            >
              <AlertDialogOverlay>
                <AlertDialogContent>
                  <AlertDialogHeader fontSize="lg">
                    Cancel bulk action
                  </AlertDialogHeader>
                  <AlertDialogBody>
                    Any {getBulkActionNoun(activity.actionType, 2)} that already
                    finished will keep their results. The rest of this action
                    will not run.
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <Button
                      variant="ghost"
                      colorScheme="secondary"
                      ref={cancelDialogRef}
                      onClick={onCloseCancel}
                      isDisabled={isCancelling}
                    >
                      Keep going
                    </Button>
                    <Button
                      colorScheme="primary"
                      onClick={handleCancel}
                      ml={3}
                      isLoading={isCancelling}
                    >
                      Cancel bulk action
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>

            <DetailFilters
              isOpen={isOpen}
              schools={activity.itemFilterOptions.schools}
              messages={activity.itemFilterOptions.messages}
              onToggleFilter={onToggle}
              onClearFilters={() => handleClearFilters()}
            />
          </AdminPage>
        );
      }}
    </RemoteDataView>
  );
};
